import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Demo from './Demo';
import FeedbackForm from './components/feedback/FeedbcakForm';
import DocumentSignModal from './components/document/DocumentSignModal';
import ProjectPicker from './components/project/ProjectPicker';

import ProjectSpeedDial from './components/project/ProjectSpeedDial';
import SideBar from './components/sidebar/SideBar';
import { Spinner } from './components';
import useStores from './stores/useStores';
import LayoutContent from './components/layout/LayoutContent';
import LayoutPanel from './components/layout/LayoutPanel';
import { SelectedBuildingProvider } from './components/editor/FeatureListContext';

const Analytics = lazy(() => import('./pages/analytics/Analytics'));
const Buildings = lazy(() => import('./pages/buildings/Buildings'));
const DataEditor = lazy(() => import('./pages/dataEditor/DataEditor'));
const MapContainer = lazy(() => import('./components/map/MapContainer'));
const Projects = lazy(() => import('./pages/projects/Projects'));

const Team = lazy(() => import('./pages/team/Team'));

const PrivateRoutes = () => {
  const location = useLocation();
  const history = useHistory();
  const { action } = history;
  const { uiStore, userStore } = useStores();

  let { layoutClass } = uiStore;

  if (location.pathname === '/onboarding') {
    layoutClass += ' onboarding';
  }

  if (userStore.user?.group?.menu_items.length === 0) {
    // layoutClass += ' sidebar-hidden';
  }

  const [previousLocation, setPreviousLocation] = useState(location);

  useEffect(() => {
    // set previousLocation if location is not modal
    if (action !== 'POP' && (!location.state || !location.state.modal)) {
      setPreviousLocation(location);
    }
  }, [location.pathname]);

  const isModal = !!(location.state?.modal && previousLocation !== location); // not initial render

  const { version } = uiStore;
  const className = `ui-${version} layout ${layoutClass}`;

  if (location.pathname.startsWith('/demo')) {
    return (
      <Route
        exact
        path={['/demo', '/demo/:buildingId']}
        render={props => <Demo props={props} />}
      />
    );
  }
  return (
    <SelectedBuildingProvider value={null}>
      <div className={className}>
        <SideBar />
        <Switch location={location}>
          <Suspense
            fallback={
              <LayoutContent
                panel={
                  <LayoutPanel>
                    <Spinner />
                  </LayoutPanel>
                }
              />
            }
          >
            <Route
              exact
              path={['/analytics', '/analytics/:analyticsId']}
              render={props => <Analytics {...props} />}
            />
            <Route
              exact
              path={[
                '/',
                '/project',
                '/project/upload',
                '/project/:projectId/tos',
                '/project/:projectId',
                '/project/:projectId/terms/:termsId',
                '/project/:projectId/preview/:fileId',
                '/project/:projectId/building/:buildingId/preview/:fileId',
                '/project/:projectId/building/:buildingId',
                '/project/:projectId/building/:buildingId/:viewId',
                '/project/:projectId/feature/:featureId',
                '/project/:projectId/building/:buildingId/:viewId/:itemId/:itemOption?',
                '/project/:projectId/:viewId',
                '/project/:projectId/:viewId/:itemId/:itemOption?'
              ]}
              render={({ match }) => <Projects match={match} />}
            />
            <Route
              path={['/search', '/search/:buildingId']}
              exact
              render={props => <Buildings {...props} />}
            />
            <Route
              path={[
                '/dataeditor',
                '/dataeditor/search',
                '/dataeditor/:datasetId',
                '/dataeditor/:datasetId/:layerId',
                '/dataeditor/:datasetId/:layerId/:featureId',
                '/dataeditor/:datasetId/:layerId/:featureId/settings'
              ]}
              exact
              render={props => <DataEditor {...props} />}
            />
            <Route
              path={['/team', '/team/:userId']}
              exact
              render={props => <Team {...props} />}
            />
          </Suspense>
        </Switch>

        <div className="layout--map">
          <Suspense fallback={<Spinner />}>
            <Route
              path={[
                '/project/:projectId/building/:buildingId',
                '/project/:projectId/feature/:featureId',
                '/project/:projectId',
                '/project',
                '/search/:buildingId',
                '/search',
                '/dataeditor/:datasetId',
                '/dataeditor/:datasetId/:layerId',
                '/dataeditor/:datasetId/:layerId/:featureId',
                '/dataeditor',
                '/'
              ]}
              render={props => <MapContainer {...props} />}
            />
          </Suspense>
        </div>
        <Switch location={location}>
          <Route
            path="/group/:groupId"
            render={({ match }) => {
              userStore.switchGroup(match.params.groupId).then(() => {
                history.push('/');
              });
            }}
          />
          <Route
            path="/documents/:documentId?"
            render={props => <DocumentSignModal {...props} />}
          />
        </Switch>
        <Route
          path={['/search', '/search/:buildingId']}
          exact
          render={props => <ProjectSpeedDial {...props} />}
        />

        {isModal ? <Route path="/" component={ProjectPicker} /> : null}
        {uiStore.feedbackModalOpen && (
          <FeedbackForm
            onClose={() => uiStore.setFeedbackModalOpen(false)}
            open={uiStore.feedbackModalOpen}
          />
        )}
      </div>
    </SelectedBuildingProvider>
  );
};

export default observer(PrivateRoutes);
